import React, { useEffect, useMemo, useState } from 'react';
import { urlApiProd } from '../utiles';
import { ThOrden, ordenarFilas, useOrdenTabla } from './ordenTabla';

const ESTADOS_BOOKING = { 0: 'Pendiente', 1: 'Solicitado', 2: 'Confirmado', 3: 'Embarcado' };
const POR_PAGINA = 15;
const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const fmtFecha = (iso) => {
    if (!iso) return '—';
    const [anio, mes, dia] = iso.split('-');
    return `${dia}/${mes}/${anio.slice(2)}`;
};

const mesDe = (iso) => (iso ? iso.slice(0, 7) : '');

const etiquetaMes = (anioMes) => {
    const [anio, mes] = anioMes.split('-');
    return `${MESES[Number(mes) - 1]} ${anio}`;
};

const porcentaje = (valor, total) => (total > 0 ? `${(valor / total) * 100}%` : '0%');

// Valores por los que se ordena cada columna. La columna Estado aún no tiene reglas, por eso no se ordena.
const ACCESORES = {
    pi: (f) => f.id_pi,
    cliente: (f) => f.cliente,
    producto: (f) => f.producto,
    etd: (f) => f.etd_objetivo,
    avance: (f) => (f.hc ? f.hc_asignado / f.hc : null),
    saldo: (f) => f.saldo_sin_booking,
    oc: (f) => f.ocs.map((oc) => oc.id_oc).join(' '),
};

const BarraAvance =({ embarcado, asignado, hc }) => {
    // Las PI antiguas no tienen HC definido: la barra se escala con lo asignado.
    const total = hc ?? asignado;
    // "asignado" del backend incluye los embarcados; la barra los separa.
    const soloAsignado = Math.max(asignado - embarcado, 0);
    const sinBooking = Math.max(total - asignado, 0);
    return (
        <>
            <div className="ce-bar">
                <div className="emb" style={{ width: porcentaje(embarcado, total) }} />
                <div className="asig" style={{ width: porcentaje(soloAsignado, total) }} />
                <div className="sin" style={{ width: porcentaje(sinBooking, total) }} />
            </div>
            <div className="ce-muted ce-mono mt-1">
                {embarcado} emb · {soloAsignado} asig · {hc ?? 'sin'} HC
            </div>
        </>
    );
};

const SubTablaBookings = ({ fila }) => {
    const pendientes = fila.saldo_sin_booking || 0;
    return (
        <div className="ce-sub">
            <table>
                <thead>
                    <tr>
                        <th>Booking</th>
                        <th>FW / Carrier</th>
                        <th>POL → POD</th>
                        <th>ETD</th>
                        <th>HC de esta PI</th>
                        <th>OC · Proveedor</th>
                        <th>Reserva</th>
                    </tr>
                </thead>
                <tbody>
                    {fila.bookings.map((b) => (
                        <tr key={b.id_solicitud}>
                            <td className="ce-mono">{b.nro_booking || `#${b.id_solicitud}`}</td>
                            <td>{[b.ffw, b.carrier].filter(Boolean).join(' · ') || '—'}</td>
                            <td>{b.pol || '—'} → {b.pod || '—'}</td>
                            <td className="ce-mono">{fmtFecha(b.etd)}</td>
                            <td className="ce-mono">{b.hc_pi} de {b.hc_booking}</td>
                            <td>
                                {b.oc_proveedor.length > 0
                                    ? b.oc_proveedor.map((oc) => `${oc.id_oc} · ${oc.proveedor}`).join(', ')
                                    : '—'}
                            </td>
                            <td>
                                <span className={`ce-pill ${b.estado === 2 || b.estado === 3 ? 'ce-pill--confirmado' : 'ce-pill--neutro'}`}>
                                    {ESTADOS_BOOKING[b.estado] ?? 'Sin estado'}
                                </span>
                            </td>
                        </tr>
                    ))}
                    {pendientes > 0 && (
                        <tr>
                            <td className="ce-mono">Por solicitar</td>
                            <td>—</td>
                            <td>—</td>
                            <td className="ce-mono">{fmtFecha(fila.etd_objetivo)}</td>
                            <td className="ce-mono">{pendientes} pendientes</td>
                            <td>
                                {fila.ocs.length > 0
                                    ? fila.ocs.map((oc) => `${oc.id_oc} · ${oc.proveedor}`).join(', ')
                                    : '—'}
                            </td>
                            <td><span className="ce-pill ce-pill--sin">Sin booking</span></td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

// version: al cambiar (p. ej. tras crear una solicitud interna) se vuelven a cargar las PI
const PlanificacionPI = ({ version = 0 }) => {
    const [filas, setFilas] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState('');
    const [expandidas, setExpandidas] = useState({});
    const [busqueda, setBusqueda] = useState('');
    const [pod, setPod] = useState('');
    const [mes, setMes] = useState('');
    const [proveedor, setProveedor] = useState('');
    const [pagina, setPagina] = useState(1);
    const [orden, alternarOrden] = useOrdenTabla();

    // Al cambiar un filtro o el orden se vuelve a la primera página
    useEffect(() => {
        setPagina(1);
    }, [busqueda, pod, mes, proveedor, orden]);

    useEffect(() => {
        let activo = true;
        const cargar = async () => {
            try {
                const token = localStorage.getItem('token');
                const respuesta = await fetch(`${urlApiProd}/controlembarques/planificacion-pi`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (!respuesta.ok) throw new Error('No se pudo cargar la planificación de PI');
                const datos = await respuesta.json();
                if (activo) setFilas(datos);
            } catch (e) {
                if (activo) setError(e.message);
            } finally {
                if (activo) setCargando(false);
            }
        };
        cargar();
        return () => { activo = false; };
    }, [version]);

    const opciones = useMemo(() => {
        const unicos = (valores) => [...new Set(valores.filter(Boolean))].sort();
        return {
            pods: unicos(filas.map((f) => f.pod)),
            meses: unicos(filas.map((f) => mesDe(f.etd_objetivo))),
            proveedores: unicos(filas.flatMap((f) => f.ocs.map((oc) => oc.proveedor))),
        };
    }, [filas]);

    const filtradas = useMemo(() => {
        const q = busqueda.trim().toLowerCase();
        return filas.filter((f) => {
            if (pod && f.pod !== pod) return false;
            if (mes && mesDe(f.etd_objetivo) !== mes) return false;
            if (proveedor && !f.ocs.some((oc) => oc.proveedor === proveedor)) return false;
            if (!q) return true;
            const texto = [
                f.id_pi, f.cliente, f.producto,
                ...f.ocs.map((oc) => oc.id_oc),
                ...f.bookings.map((b) => b.nro_booking),
            ].filter(Boolean).join(' ').toLowerCase();
            return texto.includes(q);
        });
    }, [filas, busqueda, pod, mes, proveedor]);

    const ordenadas = useMemo(() => ordenarFilas(filtradas, orden, ACCESORES), [filtradas, orden]);

    const totalPaginas = Math.max(1, Math.ceil(ordenadas.length / POR_PAGINA));
    const paginaActual = Math.min(pagina, totalPaginas);
    const desde = (paginaActual - 1) * POR_PAGINA;
    const visibles = ordenadas.slice(desde, desde + POR_PAGINA);

    const alternar = (id) => setExpandidas((actual) => ({ ...actual, [id]: !actual[id] }));

    return (
        <>
            <div className="ce-filtros">
                <label className="ce-filtro ce-filtro-buscar">
                    Buscar PI, OC, booking o cliente
                    <input
                        type="text"
                        placeholder="Ej: CTS 102-26, 4738, 45464748"
                        value={busqueda}
                        onChange={(e) => setBusqueda(e.target.value)}
                    />
                </label>
                <label className="ce-filtro">
                    Mercado / POD
                    <select value={pod} onChange={(e) => setPod(e.target.value)}>
                        <option value="">Todos</option>
                        {opciones.pods.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                </label>
                <label className="ce-filtro">
                    Mes ETD
                    <select value={mes} onChange={(e) => setMes(e.target.value)}>
                        <option value="">Todos</option>
                        {opciones.meses.map((m) => <option key={m} value={m}>{etiquetaMes(m)}</option>)}
                    </select>
                </label>
                <label className="ce-filtro">
                    Proveedor
                    <select value={proveedor} onChange={(e) => setProveedor(e.target.value)}>
                        <option value="">Todos</option>
                        {opciones.proveedores.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                </label>
                <div className="ce-leyenda">
                    <span className="emb">Embarcado</span>
                    <span className="asig">Asignado a booking</span>
                    <span className="sin">Sin booking</span>
                </div>
            </div>

            {cargando && <div className="ce-muted py-3">Cargando planificación…</div>}
            {error && <div className="alert alert-danger">{error}</div>}

            {!cargando && !error && (
                <div className="table-responsive">
                    <table className="ce-tabla">
                        <thead>
                            <tr>
                                <ThOrden clave="pi" orden={orden} alternarOrden={alternarOrden}>PI / Contrato</ThOrden>
                                <ThOrden clave="cliente" orden={orden} alternarOrden={alternarOrden}>Cliente · POD</ThOrden>
                                <ThOrden clave="producto" orden={orden} alternarOrden={alternarOrden}>Producto</ThOrden>
                                <ThOrden clave="etd" orden={orden} alternarOrden={alternarOrden}>ETD obj.</ThOrden>
                                <ThOrden clave="avance" orden={orden} alternarOrden={alternarOrden}>Avance HC</ThOrden>
                                <ThOrden clave="saldo" orden={orden} alternarOrden={alternarOrden}>Saldo sin booking</ThOrden>
                                <ThOrden clave="oc" orden={orden} alternarOrden={alternarOrden}>OC vinculadas</ThOrden>
                                <th>Estado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibles.map((fila) => {
                                const tieneDetalle = fila.bookings.length > 0 || fila.saldo_sin_booking > 0;
                                const abierta = !!expandidas[fila.id_proforma];
                                return (
                                    <React.Fragment key={fila.id_proforma}>
                                        <tr
                                            onClick={tieneDetalle ? () => alternar(fila.id_proforma) : undefined}
                                            style={tieneDetalle ? { cursor: 'pointer' } : undefined}
                                        >
                                            <td className="ce-mono">
                                                {tieneDetalle ? `${abierta ? '▾' : '▸'} ` : ''}{fila.id_pi}
                                                <div className="ce-muted">
                                                    {fila.tiene_solicitud_interna
                                                        ? fila.solicitudes_internas.map((s) => s.codigo).join(', ')
                                                        : 'Sin solicitud interna'}
                                                </div>
                                            </td>
                                            <td>
                                                {fila.cliente}
                                                <div className="ce-muted">{fila.pod || '—'}</div>
                                            </td>
                                            <td>{fila.producto || '—'}</td>
                                            <td className="ce-mono">{fmtFecha(fila.etd_objetivo)}</td>
                                            <td style={{ minWidth: 200 }}>
                                                <BarraAvance
                                                    embarcado={fila.hc_embarcado}
                                                    asignado={fila.hc_asignado}
                                                    hc={fila.hc}
                                                />
                                            </td>
                                            <td
                                                className="ce-mono"
                                                style={{ color: fila.saldo_sin_booking ? '#a3322a' : '#1d2c25', fontWeight: 700 }}
                                            >
                                                {fila.saldo_sin_booking === null ? '—' : `${fila.saldo_sin_booking} HC`}
                                            </td>
                                            <td className="ce-mono">
                                                {fila.ocs.length > 0 ? fila.ocs.map((oc) => oc.id_oc).join(' · ') : 'Sin OC'}
                                            </td>
                                            <td className="ce-muted" title="Reglas de estado por definir">—</td>
                                        </tr>
                                        {abierta && (
                                            <tr>
                                                <td colSpan={8} style={{ borderBottom: 0, padding: 0 }}>
                                                    <SubTablaBookings fila={fila} />
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                            {filtradas.length === 0 && (
                                <tr>
                                    <td colSpan={8} className="ce-muted text-center py-4">
                                        No hay PI que coincidan con los filtros.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
            {!cargando && !error && filtradas.length > 0 && (
                <div className="ce-paginacion">
                    <span className="ce-muted">
                        {desde + 1}–{desde + visibles.length} de {filtradas.length} PI
                    </span>
                    <div className="ce-paginacion-botones">
                        <button
                            type="button"
                            className="ui-btn ui-btn-secondary"
                            disabled={paginaActual === 1}
                            onClick={() => setPagina(paginaActual - 1)}
                        >
                            Anterior
                        </button>
                        <span className="ce-muted">Página {paginaActual} de {totalPaginas}</span>
                        <button
                            type="button"
                            className="ui-btn ui-btn-secondary"
                            disabled={paginaActual === totalPaginas}
                            onClick={() => setPagina(paginaActual + 1)}
                        >
                            Siguiente
                        </button>
                    </div>
                </div>
            )}
            <div className="ce-nota">
                Se listan las PI aprobadas con código. HC embarcados = HC asignados a bookings en estado Embarcado.
                Una PI puede tener varias OC y varios bookings; cada fila de booking muestra solo los HC asignados a esta PI.
                Las PI sin HC definido no muestran saldo.
            </div>
        </>
    );
};

export default PlanificacionPI;
